/** Unicode-NFC-Normalisierung (macOS-Dateinamen kommen oft als NFD). */
export function nfc(s: string): string {
  return s.normalize('NFC');
}

/** Vergleichsschlüssel: NFC, Kleinbuchstaben, Backslashes → Slashes. */
export function normKey(s: string): string {
  return nfc(s).replace(/\\/g, '/').toLowerCase();
}

/** Normalisiert einen Ordnerpfad: Slashes, keine führenden/abschließenden Slashes, kein `.`. */
export function normFolder(folder: string | null | undefined): string {
  if (!folder) return '';
  return nfc(folder)
    .replace(/\\/g, '/')
    .split('/')
    .filter((p) => p !== '' && p !== '.')
    .join('/');
}

/** Verbindet Ordner und Name zu einem Vault-Pfad. */
export function joinPath(folder: string, name: string): string {
  const f = normFolder(folder);
  return f ? `${f}/${name}` : name;
}

/** Zerlegt einen Vault-Pfad in Ordner und Dateinamen. */
export function splitPath(path: string): { folder: string; name: string } {
  const p = normFolder(path);
  const i = p.lastIndexOf('/');
  return i === -1 ? { folder: '', name: p } : { folder: p.slice(0, i), name: p.slice(i + 1) };
}

/**
 * Löst `..`/`.` in einem relativen Pfad gegen einen Basisordner auf.
 * Gibt null zurück, wenn der Pfad aus dem Vault herausführen würde.
 */
export function resolveRelative(baseFolder: string, rel: string): string | null {
  const parts = normFolder(baseFolder).split('/').filter(Boolean);
  for (const seg of nfc(rel).replace(/\\/g, '/').split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (parts.length === 0) return null;
      parts.pop();
    } else {
      parts.push(seg);
    }
  }
  return parts.join('/');
}

/** Zeichen, die in Notiz-Titeln nicht erlaubt sind (Dateisystem + Wikilink-Syntax). */
export const FORBIDDEN_TITLE_CHARS = /[\\/:*?"<>|#^[\]]/;

export function isValidTitle(title: string): boolean {
  const t = title.trim();
  return t.length > 0 && t.length <= 200 && !FORBIDDEN_TITLE_CHARS.test(t) && t !== '.' && t !== '..' && !t.startsWith('.');
}

/** Dateinamen (Anhänge): wie Titel, aber `#`, `^`, `[`, `]` sind erlaubt. */
export function isValidFileName(name: string): boolean {
  const t = name.trim();
  return t.length > 0 && t.length <= 255 && !/[\\/:*?"<>|\u0000-\u001f]/.test(t) && !t.startsWith('.');
}

export function isValidFolder(folder: string): boolean {
  if (folder === '') return true;
  return folder.split('/').every((seg) => isValidTitle(seg));
}

/** Fügt `- zeile` am Ende des Abschnitts „## Log“ ein (legt ihn bei Bedarf an). */
export function insertLogLine(body: string, line: string): string {
  const lines = body.split('\n');
  const start = lines.findIndex((l) => /^##\s+Log\s*$/i.test(l));
  if (start === -1) return `${body.replace(/\s*$/, '')}${body.trim() ? '\n\n' : ''}## Log\n\n- ${line}\n`;
  let end = lines.length;
  for (let i = start + 1; i < lines.length; i++) {
    if (/^#{1,2}\s/.test(lines[i]!)) {
      end = i;
      break;
    }
  }
  // Nach dem letzten nicht-leeren Eintrag des Abschnitts einfügen
  let at = end;
  while (at > start + 1 && lines[at - 1]!.trim() === '') at--;
  const before = lines.slice(0, at);
  if (at === start + 1) before.push('');
  let after = lines.slice(at);
  // Nur Leerzeilen danach → genau ein Zeilenende
  if (after.every((l) => l.trim() === '')) after = after.length ? [''] : [];
  return [...before, `- ${line}`, ...(after.length && after[0]!.trim() !== '' ? [''] : []), ...after].join('\n');
}
